import { Claim, Policy } from "@/types";

const OPEN_CLAIMS_KEY = "protega-open-claims";

function readOpenClaims(): Record<string, string> {
  try {
    const raw = localStorage.getItem(OPEN_CLAIMS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeOpenClaims(map: Record<string, string>) {
  try {
    localStorage.setItem(OPEN_CLAIMS_KEY, JSON.stringify(map));
  } catch {
    // Storage can be unavailable in private browsing.
  }
}

export function rememberOpenClaim(policyId: string, claimId: string) {
  const map = readOpenClaims();
  map[policyId] = claimId;
  writeOpenClaims(map);
}

export function clearOpenClaimByClaimId(claimId: string) {
  const map = readOpenClaims();
  let changed = false;
  for (const [policyId, id] of Object.entries(map)) {
    if (id === claimId) {
      delete map[policyId];
      changed = true;
    }
  }
  if (changed) writeOpenClaims(map);
}

function sameId(a?: string | null, b?: string | null): boolean {
  if (a == null || b == null) return false;
  const left = String(a).trim().toLowerCase();
  const right = String(b).trim().toLowerCase();
  if (!left || !right) return false;
  return left === right;
}

function claimBelongsToPolicy(claim: Claim, policy: Policy): boolean {
  const policyIds = [policy.id, policy.name].filter(Boolean) as string[];
  const claimIds = [claim.policyId, claim.policyNumber].filter(Boolean) as string[];

  if (policyIds.some((policyId) => claimIds.some((claimId) => sameId(policyId, claimId)))) {
    return true;
  }

  const policyUuid = String(policy.id || "")
    .replace(/^pol-/i, "")
    .toLowerCase();
  const policyTail = policyUuid.slice(-10);
  const claimPolicyId = String(claim.policyId || "")
    .replace(/^pol-/i, "")
    .toLowerCase();

  if (!policyTail || policyTail.length < 6 || !claimPolicyId) return false;
  return claimPolicyId === policyUuid || claimPolicyId.endsWith(policyTail);
}

function claimsForPolicy(policy: Policy, claims: Claim[]): Claim[] {
  return claims.filter((claim) => claimBelongsToPolicy(claim, policy));
}

function latestClaim(claims: Claim[]): Claim | undefined {
  if (claims.length === 0) return undefined;
  return [...claims].sort(
    (a, b) =>
      new Date(b.filedAt || 0).getTime() - new Date(a.filedAt || 0).getTime(),
  )[0];
}

export type PolicyClaimButton = "file" | "resume" | "view";

function storedClaimId(policyId: string): string | null {
  return readOpenClaims()[policyId] || null;
}

function isCancelledClaim(claim: Claim): boolean {
  return String(claim.status || "").trim().toLowerCase() === "cancelled";
}

/** Infer resume/view when the API omits `mode`. */
export function deriveClaimMode(
  claim: Pick<Claim, "mode" | "submitted" | "status"> | null | undefined,
): "resume" | "submitted" | undefined {
  if (!claim) return undefined;
  if (claim.mode === "resume" || claim.mode === "submitted") return claim.mode;
  if (isCancelledClaim(claim as Claim)) return undefined;
  if (claim.submitted === true) return "submitted";
  const status = String(claim.status || "").trim().toLowerCase();
  if (status && status !== "pending") return "submitted";
  if (status === "pending" || claim.submitted === false) return "resume";
  return undefined;
}

/**
 * Button state for a policy.
 * Resume/View only when a real claim exists for this policy.
 */
export function getPolicyClaimAction(
  policy: Policy,
  claims: Claim[],
): { action: PolicyClaimButton; claimId: string | null } {
  const matched = claimsForPolicy(policy, claims).filter(
    (claim) => !isCancelledClaim(claim),
  );
  const resumeClaim = latestClaim(
    matched.filter((claim) => deriveClaimMode(claim) === "resume"),
  );
  const submittedClaim = latestClaim(
    matched.filter((claim) => deriveClaimMode(claim) === "submitted"),
  );

  if (resumeClaim) {
    return { action: "resume", claimId: resumeClaim.id };
  }
  if (submittedClaim) {
    return { action: "view", claimId: submittedClaim.id };
  }

  const storedId = storedClaimId(policy.id);
  const storedClaim = storedId
    ? claims.find(
        (claim) =>
          sameId(claim.id, storedId) &&
          !isCancelledClaim(claim) &&
          claimBelongsToPolicy(claim, policy),
      )
    : undefined;
  if (storedClaim) {
    const storedMode = deriveClaimMode(storedClaim);
    if (storedMode === "submitted") {
      return { action: "view", claimId: storedClaim.id };
    }
    if (storedMode === "resume") {
      return { action: "resume", claimId: storedClaim.id };
    }
  }

  // Claims have loaded and none belong to this policy.
  if (claims.length > 0) {
    return { action: "file", claimId: null };
  }

  // Claims list is empty (none yet, or still loading): trust API only with a claim id.
  if (policy.claimId && policy.mode === "submitted") {
    return { action: "view", claimId: policy.claimId };
  }
  if (policy.claimId && policy.mode === "resume") {
    return { action: "resume", claimId: policy.claimId };
  }

  return { action: "file", claimId: null };
}
